import {
  isCompNode,
  isNetLabelNode,
  isPowerSymbolNode,
  isSpliceNode,
  NET_TERMINAL_HANDLE,
  SPLICE_HANDLE_IDS,
  type CompNode,
  type NetLabelNode,
  type PowerSymbolNode,
  type Sheet,
  type SpliceNode,
} from './store';

export type NetSource = 'power' | 'label' | 'auto';

/** Heuristic: is this net name a power / ground rail? Used for the canvas
 *  power-wire filter. Conservative — matches voltage rails, GND/PE family and
 *  explicit returns, not arbitrary signal names. */
export function isPowerNetName(name?: string | null): boolean {
  const s = (name ?? '').trim().toUpperCase();
  if (!s) return false;
  // explicit polarity rails: V+, V-, DC+, DC-
  if (/^(V|DC)\s*[+-]$/.test(s)) return true;
  // any standalone voltage token anywhere: 24V, +24V, 12VDC, 0V, 3.3V, "12V RTN"
  if (/(^|[\s_/+-])[+-]?\d+(\.\d+)?\s*V(DC|AC)?($|[\s_/+-])/.test(s)) return true;
  // keyword tokens (split on spaces / punctuation, keep Turkish letters)
  const tokens = s.split(/[^0-9A-ZÇĞİıÖŞÜ]+/).filter(Boolean);
  return tokens.some((t) => POWER_TOKENS.has(t));
}

/** Power / ground / mains keywords (uppercase). Matched per-token so combined
 *  names like "12V GND" or "MOTOR_PE" are still recognised. */
const POWER_TOKENS = new Set<string>([
  // grounds / returns
  'GND', 'GROUND', 'AGND', 'DGND', 'PGND', 'SGND', 'GNDA', 'GNDD',
  'RTN', 'RETURN', 'COM', 'COMMON',
  // protective earth / chassis
  'PE', 'PEN', 'EARTH', 'CHASSIS', 'ŞASE', 'SASE', 'TOPRAK',
  // logic / supply rails
  'VCC', 'VDD', 'VSS', 'VEE', 'VBAT', 'VBATT', 'BAT', 'BATT',
  // AC mains: line / neutral / phases
  'L', 'L1', 'L2', 'L3', 'N', 'NEUTRAL', 'NÖTR', 'NOTR', 'FAZ', 'PHASE',
]);

// Ground / return synonyms → canonical "RTN" (keeps any voltage/ISO prefix so
// isolated returns stay distinct nets). "0V" is treated as a return.
const GROUND_WORDS = new Set<string>([
  'GND', 'GROUND', 'AGND', 'DGND', 'PGND', 'SGND', 'GNDA', 'GNDD',
  'RTN', 'RETURN', '0V',
]);
// Protective-earth synonyms → canonical "PE".
const EARTH_WORDS = new Set<string>(['PE', 'PEN', 'EARTH', 'CHASSIS', 'ŞASE', 'SASE', 'TOPRAK']);

/**
 * Standardise a net name to a house style while PRESERVING anything that makes
 * a net distinct, so isolated rails don't accidentally merge:
 *   "12V GND" → "12V RTN", "24v 0V" → "24V RTN", "GND_ISO1" → "RTN ISO1",
 *   "+24 VDC" → "24VDC", "chassis" → "PE", "L"/"N" unchanged.
 * Ground/return words become RTN, earth words become PE, voltage tokens are
 * tidied; every other token is kept (uppercased) and order is maintained.
 */
export function normalizeNetName(raw?: string | null): string {
  let s = (raw ?? '').trim();
  if (!s) return '';
  s = s.replace(/(\d(?:\.\d+)?)\s*V(DC|AC)?\b/gi, (_m, n, suf) => `${n}V${(suf ?? '').toUpperCase()}`); // "24 V"/"24 vdc" → "24V"/"24VDC"
  const tokens = s.split(/[\s_/]+/).filter(Boolean);
  const out: string[] = [];
  for (const tok of tokens) {
    const u = tok.toUpperCase();
    if (GROUND_WORDS.has(u)) {
      out.push('RTN');
    } else if (EARTH_WORDS.has(u)) {
      out.push('PE');
    } else {
      const vm = u.match(/^[+-]?(\d+(?:\.\d+)?)V(DC|AC)?$/);
      out.push(vm ? `${vm[1]}V${vm[2] ?? ''}` : u);
    }
  }
  return out.filter((t, i) => i === 0 || t !== out[i - 1]).join(' ');
}

export interface NetPin {
  sheetId: string;
  sheet: string;
  nodeId: string;
  pinId: string;
  component: string;
  pinName: string;
  kind?: string;
}

export interface Net {
  /** stable id = smallest member key (survives recompute while membership holds) */
  id: string;
  name: string;
  source: NetSource;
  /** true for power nets (GND/VCC…) — merged globally across sheets */
  global: boolean;
  sheetIds: string[];
  pins: NetPin[];
  /** distinct label / power names found on this net (≥2 = conflict) */
  labelNames: string[];
  edgeIds: string[];
  nodeIds: string[];
  /** pins + label/power terminals; <2 means a floating connection */
  terminalCount: number;
}

interface KeyMeta {
  type: 'pin' | 'power' | 'label' | 'other';
  sheetId: string;
  nodeId: string;
  adjacentNodeId?: string;
  component?: string;
  pinName?: string;
  pinId?: string;
  kind?: string;
  name?: string;
}

function nearestComponentNodeId(
  label: NetLabelNode,
  components: Iterable<CompNode>,
): string | null {
  let best: { id: string; distance: number } | null = null;
  const lx = label.position.x;
  const ly = label.position.y;
  for (const comp of components) {
    const width = typeof comp.measured?.width === 'number' ? comp.measured.width : 150;
    const height = typeof comp.measured?.height === 'number' ? comp.measured.height : 90;
    const cx = Math.max(comp.position.x, Math.min(lx, comp.position.x + width));
    const cy = Math.max(comp.position.y, Math.min(ly, comp.position.y + height));
    const distance = Math.hypot(lx - cx, ly - cy);
    if (!best || distance < best.distance) best = { id: comp.id, distance };
  }
  return best && best.distance <= 44 ? best.id : null;
}

/**
 * Derive electrical nets from the schematic graph. Pure (no store access).
 * Connectivity = union-find over: drawn wires (edges), sheet-local net labels
 * sharing a name, and power symbols sharing a name (global across sheets).
 */
export function computeNets(sheets: Sheet[]): Net[] {
  const schem = sheets.filter((s) => s.kind === 'schematic');
  const sheetName = new Map(schem.map((s) => [s.id, s.name]));

  // ---- union-find over pin/terminal keys ----
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    if (!parent.has(x)) parent.set(x, x);
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r)!;
    let c = x;
    while (parent.get(c) !== r) {
      const n = parent.get(c)!;
      parent.set(c, r);
      c = n;
    }
    return r;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  const keyMeta = new Map<string, KeyMeta>();
  const powerByName = new Map<string, string[]>();
  const labelByNameSheet = new Map<string, string[]>();

  const mkKey = (sheetId: string, nodeId: string, handle: string) =>
    `${sheetId}::${nodeId}::${handle}`;

  for (const sh of schem) {
    const compById = new Map<string, CompNode>();
    const powerById = new Map<string, PowerSymbolNode>();
    const labelById = new Map<string, NetLabelNode>();
    const spliceById = new Map<string, SpliceNode>();
    for (const n of sh.nodes) {
      if (isCompNode(n)) compById.set(n.id, n);
      else if (isPowerSymbolNode(n)) powerById.set(n.id, n);
      else if (isNetLabelNode(n)) labelById.set(n.id, n);
      else if (isSpliceNode(n)) spliceById.set(n.id, n);
    }

    const register = (nodeId: string, handle: string): string => {
      const key = mkKey(sh.id, nodeId, handle);
      if (keyMeta.has(key)) return key;
      const comp = compById.get(nodeId);
      if (comp) {
        const pin = comp.data.pins.find((p) => p.id === handle);
        keyMeta.set(key, {
          type: 'pin',
          sheetId: sh.id,
          nodeId,
          component: comp.data.label,
          pinId: handle,
          pinName: pin?.name ?? handle,
          kind: pin?.kind,
        });
      } else if (powerById.has(nodeId)) {
        keyMeta.set(key, { type: 'power', sheetId: sh.id, nodeId, name: powerById.get(nodeId)!.data.name });
      } else if (labelById.has(nodeId)) {
        keyMeta.set(key, { type: 'label', sheetId: sh.id, nodeId, name: labelById.get(nodeId)!.data.name });
      } else if (spliceById.has(nodeId)) {
        keyMeta.set(key, { type: 'other', sheetId: sh.id, nodeId, name: spliceById.get(nodeId)!.data.label });
      } else {
        keyMeta.set(key, { type: 'other', sheetId: sh.id, nodeId });
      }
      return key;
    };

    // physical wires
    for (const e of sh.edges) {
      if (!e.source || !e.target) continue;
      const ka = register(e.source, e.sourceHandle ?? '');
      const kb = register(e.target, e.targetHandle ?? '');
      union(ka, kb);
      // A wire's net name is its explicit `netName`, or — falling back — its
      // visible label. Users type a label ("12V", "RS232") and expect it to BE
      // the net name; old projects only carry `label`, so treat them as one.
      const explicitNet = typeof e.data?.netName === 'string' ? e.data.netName.trim() : '';
      const wireLabel =
        typeof e.data?.label === 'string'
          ? e.data.label.trim()
          : typeof e.label === 'string'
            ? e.label.trim()
            : '';
      const edgeNetName = explicitNet || wireLabel;
      if (edgeNetName) {
        const netKey = `${sh.id}::edge-net::${e.id}`;
        keyMeta.set(netKey, {
          type: 'label',
          sheetId: sh.id,
          nodeId: `edge:${e.id}`,
          adjacentNodeId: e.source,
          name: edgeNetName,
        });
        union(ka, netKey);
        union(kb, netKey);
        const nm = `${sh.id}::${edgeNetName}`;
        (labelByNameSheet.get(nm) ?? labelByNameSheet.set(nm, []).get(nm)!).push(netKey);
      }
    }

    // collect label / power terminal keys for name-merge
    for (const l of labelById.values()) {
      const key = register(l.id, NET_TERMINAL_HANDLE);
      const adjacentNodeId = nearestComponentNodeId(l, compById.values());
      if (adjacentNodeId) {
        const meta = keyMeta.get(key);
        if (meta) meta.adjacentNodeId = adjacentNodeId;
      }
      const nm = `${sh.id}::${l.data.name}`;
      (labelByNameSheet.get(nm) ?? labelByNameSheet.set(nm, []).get(nm)!).push(key);
    }
    for (const p of powerById.values()) {
      const key = register(p.id, NET_TERMINAL_HANDLE);
      (powerByName.get(p.data.name) ?? powerByName.set(p.data.name, []).get(p.data.name)!).push(key);
    }
    for (const splice of spliceById.values()) {
      const handles = SPLICE_HANDLE_IDS.map((handle) => register(splice.id, handle));
      for (let i = 1; i < handles.length; i += 1) union(handles[0], handles[i]);
    }
  }

  // sheet-local net labels with the same name → same net
  for (const keys of labelByNameSheet.values())
    for (let i = 1; i < keys.length; i += 1) union(keys[0], keys[i]);
  // power symbols with the same name → same net, globally
  for (const keys of powerByName.values())
    for (let i = 1; i < keys.length; i += 1) union(keys[0], keys[i]);

  // ---- edges per root (for highlight) ----
  const edgesByRoot = new Map<string, Set<string>>();
  for (const sh of schem) {
    for (const e of sh.edges) {
      if (!e.source || !e.target) continue;
      const root = find(mkKey(sh.id, e.source, e.sourceHandle ?? ''));
      (edgesByRoot.get(root) ?? edgesByRoot.set(root, new Set()).get(root)!).add(e.id);
    }
  }

  // ---- assemble groups ----
  const groups = new Map<string, string[]>();
  for (const key of keyMeta.keys()) {
    const root = find(key);
    (groups.get(root) ?? groups.set(root, []).get(root)!).push(key);
  }

  const nets: Net[] = [];
  let autoSeq = 0;
  // deterministic order by smallest key
  const sortedRoots = [...groups.entries()].sort((a, b) =>
    a[1].slice().sort()[0].localeCompare(b[1].slice().sort()[0]),
  );

  for (const [, keys] of sortedRoots) {
    const sortedKeys = keys.slice().sort();
    const pins: NetPin[] = [];
    const labelNames = new Set<string>();
    const powerNames: string[] = [];
    const labelOnly: string[] = [];
    const sheetIds = new Set<string>();
    const nodeIds = new Set<string>();
    let terminalCount = 0;

    for (const key of sortedKeys) {
      const m = keyMeta.get(key)!;
      sheetIds.add(m.sheetId);
      if (!m.nodeId.startsWith('edge:')) nodeIds.add(m.nodeId);
      if (m.adjacentNodeId) nodeIds.add(m.adjacentNodeId);
      if (m.type === 'pin') {
        pins.push({
          sheetId: m.sheetId,
          sheet: sheetName.get(m.sheetId) ?? '',
          nodeId: m.nodeId,
          pinId: m.pinId ?? '',
          component: m.component ?? '?',
          pinName: m.pinName ?? '?',
          kind: m.kind,
        });
        terminalCount += 1;
      } else if (m.type === 'power') {
        if (m.name) {
          powerNames.push(m.name);
          labelNames.add(m.name);
        }
        terminalCount += 1;
      } else if (m.type === 'label') {
        if (m.name) {
          labelOnly.push(m.name);
          labelNames.add(m.name);
        }
        terminalCount += 1;
      }
    }

    const edgeIds = [...(edgesByRoot.get(find(sortedKeys[0])) ?? new Set<string>())];

    // A real net touches at least one component pin, drawn wire, or explicit net label.
    if (pins.length === 0 && edgeIds.length === 0 && labelNames.size === 0) continue;

    let name: string;
    let source: NetSource;
    let global = false;
    if (powerNames.length > 0) {
      name = powerNames[0];
      source = 'power';
      global = true;
    } else if (labelOnly.length > 0) {
      name = labelOnly[0];
      source = 'label';
    } else {
      autoSeq += 1;
      name = `N$${autoSeq}`;
      source = 'auto';
    }

    nets.push({
      id: sortedKeys[0],
      name,
      source,
      global,
      sheetIds: [...sheetIds],
      pins,
      labelNames: [...labelNames],
      edgeIds,
      nodeIds: [...nodeIds],
      terminalCount,
    });
  }

  return nets;
}

/** Flat rows for the netlist CSV / "Nets" worksheet. */
export function buildNetlistRows(nets: Net[]): string[][] {
  const rows: string[][] = [];
  for (const net of nets) {
    if (net.pins.length === 0) {
      rows.push([net.name, '', '', '']);
      continue;
    }
    for (const p of net.pins) {
      rows.push([net.name, p.component, p.pinName, p.sheet]);
    }
  }
  return rows;
}
